/**
 * API client for the Preschool Curriculum admin section (Phase 5).
 *
 * 4 entity types × {fetch, create, update, delete} = 16 functions.
 *
 * IMPORTANT: every reference to `apiEndpoints.X` lives INSIDE a function
 * body — NOT at the module top level. This avoids the TDZ
 * (Cannot-access-before-initialization) crash that the first version of
 * this file caused under Rollup's stricter production module init order.
 * Mirror centre.js — each function is explicit.
 *
 * Backend endpoints (shipped in Phase 1):
 *   /api/v1/preschool-evaluation/admin/skills/
 *   /api/v1/preschool-evaluation/admin/weekly-themes/
 *   /api/v1/preschool-evaluation/admin/weekly-skill-plans/
 *   /api/v1/preschool-evaluation/admin/monthly-themes/
 */
import { isEmpty } from 'lodash'

import {
  apiEndpoints,
  validateDeleteRequest,
  validateUpdateRequest
} from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, put, remove } from '../request'

// ---- request-shape helpers ----

const skillRequestStructure = (data) => ({
  name: data.name,
  type: data.type,
  order: data.order != null ? Number(data.order) : 0,
  code: data.code || null,
  is_active: data.is_active !== false && data.is_active !== 'false'
})

const weeklyThemeRequestStructure = (data) => ({
  level: data.level,
  month_key: data.month_key,
  week_start_date: data.week_start_date,
  week_end_date: data.week_end_date,
  subtheme_title: data.subtheme_title || '',
  subtheme_topics: data.subtheme_topics || '',
  is_published: data.is_published === true
})

const weeklySkillPlanRequestStructure = (data) => ({
  level: data.level,
  month_key: data.month_key,
  week_start_date: data.week_start_date,
  week_end_date: data.week_end_date,
  selected_skill_ids: parseJsonOrDefault(data.selected_skill_ids, []),
  spiral: parseJsonOrDefault(data.spiral, []),
  sequential: parseJsonOrDefault(data.sequential, []),
  sequential_focus_by_skill_id: parseJsonOrDefault(
    data.sequential_focus_by_skill_id,
    {}
  ),
  numeric_focus: data.numeric_focus != null ? Number(data.numeric_focus) : null,
  is_published: data.is_published === true
})

const monthlyThemeRequestStructure = (data) => ({
  level: data.level,
  month_key: data.month_key,
  theme: data.theme || '',
  is_published: data.is_published === true
})

const parseJsonOrDefault = (raw, fallback) => {
  if (raw == null || raw === '') return fallback
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch (e) {
    throw new Error(
      `Invalid JSON in field: ${e.message}. Fix the JSON or leave the field blank.`
    )
  }
}

// The 3 JSON fields come back from the API as parsed arrays/objects. The
// admin renders them in a mui-datatables table (which can't render an object
// as a cell — it crashes the page) and edits them as raw JSON text. So
// stringify them on fetch: the table shows readable JSON, the edit form is
// editable text, and weeklySkillPlanRequestStructure parses them back on save.
const PLAN_JSON_FIELDS = [
  'selected_skill_ids',
  'spiral',
  'sequential',
  'sequential_focus_by_skill_id'
]

const stringifyPlanJsonFields = (plan) => {
  if (!plan || typeof plan !== 'object') return plan
  const next = { ...plan }
  PLAN_JSON_FIELDS.forEach((field) => {
    if (next[field] != null && typeof next[field] === 'object') {
      next[field] = JSON.stringify(next[field])
    }
  })
  return next
}

const stringifyPlanResponse = (data) => {
  if (Array.isArray(data)) return data.map(stringifyPlanJsonFields)
  if (data && Array.isArray(data.results)) {
    return { ...data, results: data.results.map(stringifyPlanJsonFields) }
  }
  return stringifyPlanJsonFields(data)
}

// =========================================================================
// Skill
// =========================================================================

const fetchPreschoolSkill = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolSkill, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching preschool skill :- ', error.message)
    throw error
  }
}

const createPreschoolSkill = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.preschoolSkill}/`,
      skillRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in creating preschool skill :- ', error.message)
    throw error
  }
}

const updatePreschoolSkill = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.preschoolSkill}/${data.uuid}/`,
      skillRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in updating preschool skill :- ', error.message)
    throw error
  }
}

const deletePreschoolSkill = async ({ ids }) => {
  validateDeleteRequest(ids)
  try {
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints.preschoolSkill}/${id}/`))
    )
    return ids
  } catch (error) {
    console.log('error in deleting preschool skill :- ', error.message)
    throw error
  }
}

// =========================================================================
// WeeklyTheme
// =========================================================================

const fetchPreschoolWeeklyTheme = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolWeeklyTheme, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching preschool weekly theme :- ', error.message)
    throw error
  }
}

const createPreschoolWeeklyTheme = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.preschoolWeeklyTheme}/`,
      weeklyThemeRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in creating preschool weekly theme :- ', error.message)
    throw error
  }
}

const updatePreschoolWeeklyTheme = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.preschoolWeeklyTheme}/${data.uuid}/`,
      weeklyThemeRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in updating preschool weekly theme :- ', error.message)
    throw error
  }
}

const deletePreschoolWeeklyTheme = async ({ ids }) => {
  validateDeleteRequest(ids)
  try {
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints.preschoolWeeklyTheme}/${id}/`))
    )
    return ids
  } catch (error) {
    console.log('error in deleting preschool weekly theme :- ', error.message)
    throw error
  }
}

// =========================================================================
// WeeklySkillPlan
// =========================================================================

const fetchPreschoolWeeklySkillPlan = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolWeeklySkillPlan, queries)
  try {
    const response = await get(requestUrl)
    return stringifyPlanResponse(response.data)
  } catch (error) {
    console.log(
      'error in fetching preschool weekly skill plan :- ',
      error.message
    )
    throw error
  }
}

// Raw fetch (parsed JSON, NOT stringified) for the Week Builder UI, which
// needs selected_skill_ids / spiral / sequential_focus_by_skill_id as real
// arrays/objects to populate checkboxes + goal editors.
const fetchPreschoolWeeklySkillPlanForWeek = async ({
  level,
  week_start_date
}) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.preschoolWeeklySkillPlan, {
        level,
        week_start_date
      })
    )
    return response.data
  } catch (error) {
    console.log(
      'error in fetching preschool weekly skill plan for week :- ',
      error.message
    )
    throw error
  }
}

const createPreschoolWeeklySkillPlan = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.preschoolWeeklySkillPlan}/`,
      weeklySkillPlanRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log(
      'error in creating preschool weekly skill plan :- ',
      error.message
    )
    throw error
  }
}

const updatePreschoolWeeklySkillPlan = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.preschoolWeeklySkillPlan}/${data.uuid}/`,
      weeklySkillPlanRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log(
      'error in updating preschool weekly skill plan :- ',
      error.message
    )
    throw error
  }
}

const deletePreschoolWeeklySkillPlan = async ({ ids }) => {
  validateDeleteRequest(ids)
  try {
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints.preschoolWeeklySkillPlan}/${id}/`))
    )
    return ids
  } catch (error) {
    console.log(
      'error in deleting preschool weekly skill plan :- ',
      error.message
    )
    throw error
  }
}

// =========================================================================
// MonthlyTheme
// =========================================================================

const fetchPreschoolMonthlyTheme = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolMonthlyTheme, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching preschool monthly theme :- ', error.message)
    throw error
  }
}

const createPreschoolMonthlyTheme = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.preschoolMonthlyTheme}/`,
      monthlyThemeRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in creating preschool monthly theme :- ', error.message)
    throw error
  }
}

const updatePreschoolMonthlyTheme = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.preschoolMonthlyTheme}/${data.uuid}/`,
      monthlyThemeRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in updating preschool monthly theme :- ', error.message)
    throw error
  }
}

const deletePreschoolMonthlyTheme = async ({ ids }) => {
  validateDeleteRequest(ids)
  try {
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints.preschoolMonthlyTheme}/${id}/`))
    )
    return ids
  } catch (error) {
    console.log('error in deleting preschool monthly theme :- ', error.message)
    throw error
  }
}

// =========================================================================
// =========================================================================
// AnnualLO + SpiralLO (catalog)
// =========================================================================

const annualLoRequestStructure = (data) => ({
  skill: data.skill,
  level: data.level,
  name: data.name,
  text: data.text || '',
  order: data.order != null ? Number(data.order) : 0,
  is_active: data.is_active !== false && data.is_active !== 'false'
})

const spiralLoRequestStructure = (data) => ({
  skill: data.skill,
  level: data.level,
  month: data.month != null ? Number(data.month) : null,
  annual_lo: data.annual_lo || null,
  is_culmination:
    data.is_culmination === true || data.is_culmination === 'true',
  annual_lo_inferred:
    data.annual_lo_inferred === true || data.annual_lo_inferred === 'true',
  name: data.name,
  text: data.text || '',
  order_in_month: data.order_in_month != null ? Number(data.order_in_month) : 0,
  is_active: data.is_active !== false && data.is_active !== 'false'
})

const fetchPreschoolAnnualLo = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolAnnualLo, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching preschool annual LO :- ', error.message)
    throw error
  }
}

const createPreschoolAnnualLo = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.preschoolAnnualLo}/`,
      annualLoRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in creating preschool annual LO :- ', error.message)
    throw error
  }
}

const updatePreschoolAnnualLo = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.preschoolAnnualLo}/${data.uuid}/`,
      annualLoRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in updating preschool annual LO :- ', error.message)
    throw error
  }
}

const deletePreschoolAnnualLo = async ({ ids }) => {
  validateDeleteRequest(ids)
  try {
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints.preschoolAnnualLo}/${id}/`))
    )
    return ids
  } catch (error) {
    console.log('error in deleting preschool annual LO :- ', error.message)
    throw error
  }
}

const fetchPreschoolSpiralLo = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolSpiralLo, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching preschool spiral LO :- ', error.message)
    throw error
  }
}

const createPreschoolSpiralLo = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.preschoolSpiralLo}/`,
      spiralLoRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in creating preschool spiral LO :- ', error.message)
    throw error
  }
}

const updatePreschoolSpiralLo = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.preschoolSpiralLo}/${data.uuid}/`,
      spiralLoRequestStructure(data)
    )
    return response.data
  } catch (error) {
    console.log('error in updating preschool spiral LO :- ', error.message)
    throw error
  }
}

const deletePreschoolSpiralLo = async ({ ids }) => {
  validateDeleteRequest(ids)
  try {
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints.preschoolSpiralLo}/${id}/`))
    )
    return ids
  } catch (error) {
    console.log('error in deleting preschool spiral LO :- ', error.message)
    throw error
  }
}

// =========================================================================
// Teacher authoring (weekly evaluation) — same endpoints the mobile app uses.
// Note: these require a *teacher* auth token (IsTeacher). Under the current
// admin login they 403 by design; they go live once teacher login lands.
// =========================================================================

const fetchPreschoolCentres = async () => {
  const requestUrl = makeRequestUrl(apiEndpoints.centre, { limit: 200 })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching centres :- ', error.message)
    throw error
  }
}

const fetchTeacherWeeklyBundle = async ({
  centre,
  level,
  week_start_date
} = {}) => {
  const requestUrl = makeRequestUrl(apiEndpoints.preschoolTeacherWeeklyBundle, {
    centre,
    level,
    week_start_date
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching teacher weekly bundle :- ', error.message)
    throw error
  }
}

// Admin/super-admin read-only view of a cohort's submitted evaluations. Returns
// the same bundle shape as the teacher bundle, but existing_evaluations covers
// ALL teachers for the (centre, level, week) — see oh-api admin_readonly.py.
const fetchAdminWeeklyBundle = async ({
  centre,
  level,
  week_start_date
} = {}) => {
  const requestUrl = makeRequestUrl(apiEndpoints.preschoolAdminWeeklyBundle, {
    centre,
    level,
    week_start_date
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching admin weekly bundle :- ', error.message)
    throw error
  }
}

// Photos-only admin write: attach/detach one image URL on an observation
// (goal_id) of a student's weekly evaluation. The backend mirrors it into the
// parent-facing snapshot. payload: { student, week_start_date, goal_id, action, url }
const saveAdminEvaluationPhoto = async (payload) => {
  try {
    const response = await post(
      apiEndpoints.preschoolAdminEvaluationPhoto,
      payload
    )
    return response.data
  } catch (error) {
    console.log('error in saving admin evaluation photo :- ', error.message)
    throw error
  }
}

const upsertTeacherWeeklyEvaluation = async (payload) => {
  try {
    const response = await put(
      apiEndpoints.preschoolTeacherWeeklyEvaluation,
      payload
    )
    return response.data
  } catch (error) {
    console.log('error in saving teacher weekly evaluation :- ', error.message)
    throw error
  }
}

const fetchTeacherTermReport = async ({ student, term_key } = {}) => {
  const requestUrl = makeRequestUrl(apiEndpoints.preschoolTeacherTermReport, {
    student,
    term_key
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching teacher term report :- ', error.message)
    throw error
  }
}

// Handles save (sections/notes), submit (publish) and reopen — all via the
// same PUT, distinguished by the payload flags.
const upsertTeacherTermReport = async (payload) => {
  try {
    const response = await put(apiEndpoints.preschoolTeacherTermReport, payload)
    return response.data
  } catch (error) {
    console.log('error in saving teacher term report :- ', error.message)
    throw error
  }
}

// v2 term report ("term progress") — the same endpoint the teacher app uses.
// GET returns the Annual LOs (grouped by skill) with per-term ratings, the three
// terms + their weekly gate, and per-skill notes seeded from that term's weekly
// evaluations. week_start_date only fixes the academic year.
const fetchTeacherTermProgress = async ({ student, week_start_date } = {}) => {
  const requestUrl = makeRequestUrl(apiEndpoints.preschoolTeacherTermProgress, {
    student,
    week_start_date
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log('error in fetching teacher term progress :- ', error.message)
    throw error
  }
}

// PUT one term: { student, term_key, ratings:[{annual_lo, rating}],
// skill_notes:[{skill_id, text, image_urls}], publish|unpublish }. Gated — 409
// until every weekly evaluation for the term is submitted.
const saveTeacherTermProgress = async (payload) => {
  try {
    const response = await put(
      apiEndpoints.preschoolTeacherTermProgress,
      payload
    )
    return response.data
  } catch (error) {
    console.log('error in saving teacher term progress :- ', error.message)
    throw error
  }
}

// Photos-only admin write for a TERM report per-skill note (no teacher-login
// blocker; centre-scoped server-side). payload: { student, term_key, skill_id,
// action, url }
const saveAdminTermNotePhoto = async (payload) => {
  try {
    const response = await post(apiEndpoints.preschoolAdminTermPhoto, payload)
    return response.data
  } catch (error) {
    console.log('error in saving admin term note photo :- ', error.message)
    throw error
  }
}

// Exports
// =========================================================================
export {
  fetchPreschoolCentres,
  fetchTeacherWeeklyBundle,
  fetchAdminWeeklyBundle,
  saveAdminEvaluationPhoto,
  upsertTeacherWeeklyEvaluation,
  fetchTeacherTermReport,
  upsertTeacherTermReport,
  fetchTeacherTermProgress,
  saveTeacherTermProgress,
  saveAdminTermNotePhoto,
  fetchPreschoolSkill,
  createPreschoolSkill,
  updatePreschoolSkill,
  deletePreschoolSkill,
  fetchPreschoolWeeklyTheme,
  createPreschoolWeeklyTheme,
  updatePreschoolWeeklyTheme,
  deletePreschoolWeeklyTheme,
  fetchPreschoolWeeklySkillPlan,
  fetchPreschoolWeeklySkillPlanForWeek,
  createPreschoolWeeklySkillPlan,
  updatePreschoolWeeklySkillPlan,
  deletePreschoolWeeklySkillPlan,
  fetchPreschoolMonthlyTheme,
  createPreschoolMonthlyTheme,
  updatePreschoolMonthlyTheme,
  deletePreschoolMonthlyTheme,
  fetchPreschoolAnnualLo,
  createPreschoolAnnualLo,
  updatePreschoolAnnualLo,
  deletePreschoolAnnualLo,
  fetchPreschoolSpiralLo,
  createPreschoolSpiralLo,
  updatePreschoolSpiralLo,
  deletePreschoolSpiralLo
}
